import React from 'react';

const TechnicalSpecifications = ({
  asset,
  isEditing,
  onUpdate
}) => {

  // =====================================
  // HANDLE CHANGE
  // =====================================

  const handleSpecChange = (field, value) => {
    onUpdate({
      ...asset,
      [field]: value
    });
  };

  const inputClass = (editable = isEditing) =>
    `w-full border rounded-lg p-3 text-sm transition-colors ${
      !editable
        ? 'border-border bg-muted/50 text-muted-foreground cursor-not-allowed'
        : 'border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary'
    }`;

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">
          Technical Specifications
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Hardware components, operating system, and installed software details
        </p>
      </div>

      {/* HARDWARE GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* PROCESSOR */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Processor / CPU
          </label>
          <input
            type="text"
            value={asset?.processor || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('processor', e.target.value)}
            className={inputClass()}
            placeholder="e.g. Apple M3 Pro / Intel Core i7-13700H"
          />
        </div>

        {/* RAM */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            RAM / Memory
          </label>
          <select
            value={asset?.ram || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('ram', e.target.value)}
            className={inputClass()}
          >
            <option value="">Select RAM</option>
            <option value="4GB">4 GB</option>
            <option value="8GB">8 GB</option>
            <option value="16GB">16 GB</option>
            <option value="32GB">32 GB</option>
            <option value="64GB">64 GB</option>
            <option value="128GB">128 GB</option>
          </select>
        </div>

        {/* STORAGE */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Storage
          </label>
          <input
            type="text"
            value={asset?.storage || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('storage', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 512GB NVMe SSD"
          />
        </div>

        {/* GRAPHICS */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Graphics Card (GPU)
          </label>
          <input
            type="text"
            value={asset?.graphics || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('graphics', e.target.value)}
            className={inputClass()}
            placeholder="e.g. NVIDIA RTX 4070 / Integrated GPU"
          />
        </div>

        {/* DISPLAY SIZE */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Display Size
          </label>
          <input
            type="text"
            value={asset?.display_size || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('display_size', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 16-inch Liquid Retina XDR"
          />
        </div>

        {/* RESOLUTION */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Screen Resolution
          </label>
          <input
            type="text"
            value={asset?.resolution || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('resolution', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 3456 x 2234 or 1920x1080"
          />
        </div>

        {/* OPERATING SYSTEM */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Operating System
          </label>
          <select
            value={asset?.operating_system || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('operating_system', e.target.value)}
            className={inputClass()}
          >
            <option value="">Select OS</option>
            <option value="Windows 11 Pro">Windows 11 Pro</option>
            <option value="Windows 11 Home">Windows 11 Home</option>
            <option value="Windows 10 Pro">Windows 10 Pro</option>
            <option value="macOS Sonoma">macOS Sonoma</option>
            <option value="macOS Sequoia">macOS Sequoia</option>
            <option value="Ubuntu 22.04 LTS">Ubuntu 22.04 LTS</option>
            <option value="Ubuntu 24.04 LTS">Ubuntu 24.04 LTS</option>
            <option value="Red Hat Enterprise Linux">Red Hat Enterprise Linux</option>
            <option value="Android">Android</option>
            <option value="iOS">iOS</option>
          </select>
        </div>

        {/* OS VERSION */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            OS Version / Build
          </label>
          <input
            type="text"
            value={asset?.os_version || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('os_version', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 23H2 (Build 22631)"
          />
        </div>

        {/* NETWORK */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Network & Connectivity
          </label>
          <input
            type="text"
            value={asset?.network_connectivity || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('network_connectivity', e.target.value)}
            className={inputClass()}
            placeholder="e.g. Wi-Fi 6E (802.11ax), Bluetooth 5.3, Gigabit Ethernet"
          />
        </div>

        {/* PORTS */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Ports & Interfaces
          </label>
          <input
            type="text"
            value={asset?.ports || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('ports', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 3x Thunderbolt 4, HDMI 2.1, SDXC slot"
          />
        </div>

        {/* POWER */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Power Requirements
          </label>
          <input
            type="text"
            value={asset?.power_requirements || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('power_requirements', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 140W USB-C Power Adapter"
          />
        </div>

        {/* DIMENSIONS */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Physical Dimensions & Weight
          </label>
          <input
            type="text"
            value={asset?.dimensions || ''}
            disabled={!isEditing}
            onChange={(e) => handleSpecChange('dimensions', e.target.value)}
            className={inputClass()}
            placeholder="e.g. 35.57 x 24.81 x 1.68 cm, 2.16 kg"
          />
        </div>
      </div>

      {/* SOFTWARE SECTION */}
      <div className="mt-8 pt-6 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Software & Tools
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ANTIVIRUS */}
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground">
              Antivirus / Endpoint Protection
            </label>
            <input
              type="text"
              value={asset?.antivirus || ''}
              disabled={!isEditing}
              onChange={(e) => handleSpecChange('antivirus', e.target.value)}
              className={inputClass()}
              placeholder="e.g. CrowdStrike Falcon, Windows Defender"
            />
          </div>

          {/* OFFICE */}
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground">
              Productivity / Office Suite
            </label>
            <input
              type="text"
              value={asset?.office_suite || ''}
              disabled={!isEditing}
              onChange={(e) => handleSpecChange('office_suite', e.target.value)}
              className={inputClass()}
              placeholder="e.g. Microsoft 365 Enterprise, Google Workspace"
            />
          </div>

          {/* DEVELOPMENT */}
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground">
              Development & Engineering Tools
            </label>
            <input
              type="text"
              value={asset?.development_tools || ''}
              disabled={!isEditing}
              onChange={(e) => handleSpecChange('development_tools', e.target.value)}
              className={inputClass()}
              placeholder="e.g. VS Code, Docker Desktop, Git, Node.js"
            />
          </div>

          {/* OTHER SOFTWARE */}
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground">
              Other Licensed Software
            </label>
            <input
              type="text"
              value={asset?.other_software || ''}
              disabled={!isEditing}
              onChange={(e) => handleSpecChange('other_software', e.target.value)}
              className={inputClass()}
              placeholder="e.g. Adobe Creative Cloud, Slack, Zoom"
            />
          </div>
        </div>
      </div>

      {/* TECHNICAL NOTES */}
      <div className="mt-8 pt-6 border-t border-border">
        <label className="block text-sm font-medium mb-1.5 text-foreground">
          Technical Notes & Configuration Details
        </label>
        <textarea
          rows={4}
          value={asset?.technical_notes || ''}
          disabled={!isEditing}
          onChange={(e) => handleSpecChange('technical_notes', e.target.value)}
          className={inputClass()}
          placeholder="Enter BIOS version, MAC addresses, customized partitioning, or specific hardware modifications..."
        />
      </div>
    </div>
  );
};

export default TechnicalSpecifications;
